import React, { useEffect, useMemo, useRef, useState } from "react";
import "./UserSelects.css";

/**
 * The "Send To" control — the create / edit schedule page, and "Share with" in
 * the Share dialog on the Schedule Logs page.
 *
 * Two modes, decided by whether a `users` list is passed:
 *
 *   with a list     search picks people out of it, and an address that is not
 *                   in it can still be typed in
 *   without a list  pure email entry — no dropdown at all
 *
 * The create page runs in the second mode: there is no user API yet, so a
 * hardcoded list would only offer people who may not exist. The Share dialog
 * still passes its list. When a real user API lands, passing it here again
 * restores the dropdown with no other change.
 *
 * Either way, addresses are added with Enter, comma or semicolon (or by pasting
 * a separated list), and sit under the field as removable chips.
 */

/* Loose on purpose: this is a "did you mean to type an address" check, not an
   attempt to decide whether a mailbox exists. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const initialsOf = (value) =>
  String(value || "")
    .split(/[\s.@]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

/* A stable colour per address, so the same person is the same colour on every
   visit — a random one would change on each render. */
const SWATCHES = ["#7c3aed", "#059669", "#2563eb", "#d97706", "#db2777", "#0891b2", "#dc2626", "#16a34a"];
const colorFor = (seed) => {
  let hash = 0;
  for (let i = 0; i < String(seed).length; i++) hash = (hash * 31 + String(seed).charCodeAt(i)) % 9973;
  return SWATCHES[hash % SWATCHES.length];
};

const Avatar = ({ name, email, color }) => (
  <span className="csh-avatar" style={{ background: color || colorFor(email || name) }}>
    {initialsOf(name || email)}
  </span>
);

export default function RecipientSelect({ users = [], value = [], onChange, placeholder }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const wrapRef = useRef(null);

  // No list to search means no dropdown, and the field is for addresses only.
  const hasList = users.length > 0;
  const fieldPlaceholder = placeholder || (hasList ? "Search or enter email..." : "Enter email address...");

  // Clicking anywhere else closes the list; the field keeps whatever was typed.
  useEffect(() => {
    if (!open) return undefined;
    const onDocumentMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocumentMouseDown);
    return () => document.removeEventListener("mousedown", onDocumentMouseDown);
  }, [open]);

  const chosenEmails = useMemo(() => new Set(value.map((r) => r.email)), [value]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter(
      (u) =>
        !chosenEmails.has(u.email) &&
        (q === "" || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)),
    );
  }, [users, query, chosenEmails]);

  const add = (recipient) => {
    if (chosenEmails.has(recipient.email)) return;
    onChange([...value, recipient]);
    setQuery("");
    setError("");
  };

  const remove = (email) => {
    onChange(value.filter((r) => r.email !== email));
  };

  const addEmail = (raw) => {
    const email = raw.trim().toLowerCase();
    if (!email) return true;
    if (!EMAIL_RE.test(email)) return false;
    if (chosenEmails.has(email)) return true;
    add({ id: `email-${email}`, name: email, email, external: true });
    return true;
  };

  /* Commits whatever is in the field. One or many — pasting
     "a@x.com, b@y.com" and pressing Enter should add both, not fail as one
     malformed address. Bad entries stay behind so they can be corrected. */
  const commit = () => {
    const typed = query.trim();
    if (!typed) return true;

    // With a list, a search narrowed to exactly one person takes that person.
    if (hasList && matches.length === 1 && !typed.includes(",") && !typed.includes(";")) {
      add(matches[0]);
      return true;
    }

    const parts = typed.split(/[,;]+/).map((p) => p.trim()).filter(Boolean);
    const rejected = parts.filter((part) => !addEmail(part));

    if (rejected.length > 0) {
      setQuery(rejected.join(", "));
      setError(
        hasList
          ? "Pick someone from the list, or type a full email address."
          : "Enter a full email address, e.g. name@company.com",
      );
      return false;
    }
    setQuery("");
    setError("");
    return true;
  };

  const handleKeyDown = (e) => {
    // Comma and semicolon end an address, the way every mail client does it.
    if (e.key === "Enter" || e.key === "," || e.key === ";") {
      e.preventDefault();
      commit();
      return;
    }
    // Backspace on an empty field takes the last chip back.
    if (e.key === "Backspace" && query === "" && value.length > 0) {
      remove(value[value.length - 1].email);
    }
  };

  return (
    <div className="csh-recipients" ref={wrapRef}>
      <div className={`csh-recipient-field ${open ? "is-open" : ""}`}>
        <span className="csh-recipient-search-icon">
          <svg viewBox="0 0 20 20" width="15" height="15" fill="none" aria-hidden="true">
            <circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M13 13l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </span>
        <input
          type="text"
          className="csh-recipient-input"
          placeholder={fieldPlaceholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setError("");
            if (hasList) setOpen(true);
          }}
          onFocus={() => hasList && setOpen(true)}
          onKeyDown={handleKeyDown}
          /* Clicking away commits what was typed. Without this, filling the
             field and pressing Publish silently drops the recipient. */
          onBlur={commit}
        />
        {/* A caret with no dropdown behind it is a lie, so it only shows when
            there is a list to open. */}
        {hasList && (
          <span className="csh-recipient-caret" aria-hidden="true">
            <svg viewBox="0 0 12 8" width="10" height="7" fill="none">
              <path d="M1 1l5 5 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </span>
        )}
      </div>

      {hasList && open && (
        <div className="csh-recipient-menu">
          {matches.length > 0 && <div className="csh-recipient-menu-head">Users</div>}
          {matches.map((u) => (
            <button type="button" className="csh-recipient-option" key={u.id} onClick={() => add(u)}>
              <Avatar name={u.name} email={u.email} color={u.color} />
              <span className="csh-recipient-option-text">
                <span className="csh-recipient-option-name">{u.name}</span>
                <span className="csh-recipient-option-email">{u.email}</span>
              </span>
            </button>
          ))}
          {matches.length === 0 && (
            <div className="csh-recipient-hint">
              {EMAIL_RE.test(query.trim())
                ? "Press Enter to add this email address."
                : "No matching users. Type a full email address to add someone new."}
            </div>
          )}
        </div>
      )}

      {error && <div className="csh-recipient-error">{error}</div>}

      {value.length > 0 && (
        <ul className="csh-recipient-list">
          {value.map((r) => (
            <li className={`csh-recipient-chip ${r.external ? "is-external" : ""}`} key={r.email}>
              <Avatar name={r.name} email={r.email} color={r.color} />
              <span className="csh-recipient-chip-name">{r.external ? r.email : r.name}</span>
              <button
                type="button"
                className="csh-recipient-remove"
                onClick={() => remove(r.email)}
                aria-label={`Remove ${r.name}`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
